import { useCallback, useMemo } from 'react';
import { ApiError, NetworkError } from '@cbhq/data-layer';
import type { WorkspaceMembersResult } from '@cbhq/identity-api-hooks/account-management/fetch';
import {
  useRefreshWorkspaceMembers,
  useWithOptimisticWorkspaceMembersUpdates,
} from '@cbhq/identity-api-hooks/account-management/fetch';
import {
  useAddOrganizationMember,
  useBulkCancelInvites,
  useCancelPendingRoleChange,
  useRemoveOrganizationMember,
  useUpdateOrganizationMemberRole,
} from '@cbhq/identity-api-hooks/account-management/mutation';
import type {
  CoinbaseEngUsersAccountManagementApiApiCancelPendingRoleChangeResult,
  CoinbaseEngUsersAccountManagementApiApiWorkspaceMember,
} from '@cbhq/identity-api-hooks/account-management/types';
import {
  CoinbaseEngUsersAccountManagementApiApiCancelPendingRoleChangeOutcome as CancelOutcome,
  CoinbaseEngUsersAccountManagementApiApiRoleActivationState as RoleActivationState,
} from '@cbhq/identity-api-hooks/account-management/types';

import { useOrganization } from '../../../contexts/OrganizationContext';
import { logError } from '../../../utils/logging';
import { isStepUpProofTokenError } from '../../../utils/stepUpProofToken';
import { CANCEL_PENDING_ROLE_ERROR_CODES } from '../utils/cancelPendingRoleErrors';
import type { MemberRole, MemberRolesForEntity } from '../utils/constants';
import { getMemberRolesByEntity } from '../utils/workspaceTeam';

type OptimisticData = Readonly<WorkspaceMembersResult | undefined>;
type MutationFn = (currentValue: OptimisticData) => [OptimisticData, Promise<unknown>];

/** "cancellation_pending": the backend recorded the cancellation, but in-flight work may still grant the role. */
export type CancelPendingRolesOutcome = 'cancelled' | 'cancellation_pending';

/** Request IDs of the member's roles the backend still reports as pending. */
function getPendingRequestIds(
  member: CoinbaseEngUsersAccountManagementApiApiWorkspaceMember | undefined,
): string[] {
  return (member?.legalEntityRolesDetailed ?? []).flatMap(({ rolesDetailed }) =>
    (rolesDetailed ?? []).flatMap(({ requestId, activationState }) =>
      requestId && activationState === RoleActivationState.ROLE_ACTIVATION_STATE_SCHEDULED
        ? [requestId]
        : [],
    ),
  );
}

/**
 * Reads the per-request results of CancelPendingRoleChange. Throws when a role was
 * already granted (`CANCEL_PENDING_ROLE_ERROR_CODES.alreadyApplied`), when a
 * request has no confirmed outcome, or when a request in `knownRequestIds` has no
 * result: discovery is eventually consistent, and a missing result is not a
 * cancellation.
 */
function getCancelPendingRolesOutcome(
  results: readonly CoinbaseEngUsersAccountManagementApiApiCancelPendingRoleChangeResult[] = [],
  knownRequestIds: readonly string[] = [],
): CancelPendingRolesOutcome {
  if (
    results.some(
      ({ outcome }) => outcome === CancelOutcome.CANCEL_PENDING_ROLE_CHANGE_OUTCOME_ALREADY_APPLIED,
    )
  ) {
    throw new Error(CANCEL_PENDING_ROLE_ERROR_CODES.alreadyApplied);
  }
  const unconfirmed = results.find(
    ({ outcome }) =>
      outcome == null || outcome === CancelOutcome.CANCEL_PENDING_ROLE_CHANGE_OUTCOME_UNSPECIFIED,
  );
  if (unconfirmed) {
    throw new Error(unconfirmed.error || 'Failed to revoke admin access');
  }
  const reportedRequestIds = new Set(results.map(({ requestId }) => requestId));
  if (knownRequestIds.some((requestId) => !reportedRequestIds.has(requestId))) {
    throw new Error('Failed to revoke admin access');
  }
  return results.some(
    ({ outcome }) =>
      outcome === CancelOutcome.CANCEL_PENDING_ROLE_CHANGE_OUTCOME_CANCELLATION_PENDING,
  )
    ? 'cancellation_pending'
    : 'cancelled';
}

function dedupe(entityIds: string[]): string[] {
  return [...new Set(entityIds)];
}

/** Whether `nextRoles` differs from `currentRoles` as a set. A missing entry counts as changed. */
function haveRolesChanged(
  currentRoles: MemberRole[] | undefined,
  nextRoles: MemberRole[],
): boolean {
  if (!currentRoles || currentRoles.length !== nextRoles.length) return true;
  const currentRolesSet = new Set(currentRoles);
  return nextRoles.some((role) => !currentRolesSet.has(role));
}

function getCurrentRolesByEntityId(
  data: OptimisticData,
  memberUuid: string,
): Map<string, MemberRole[]> {
  const member = data?.members?.find((m) => m.userUuid === memberUuid);
  return member ? getMemberRolesByEntity(member) : new Map();
}

function throwIfError<T>(response: T): asserts response is Exclude<T, NetworkError | ApiError> {
  if (response instanceof NetworkError) {
    throw new Error('Network error. Please check your connection.', { cause: response });
  }
  if (response instanceof ApiError) {
    throw new Error(response.message, { cause: response });
  }
}

type CancelInviteParams = {
  userId: string | undefined;
  email: string;
  legalEntityIds: string[];
};

// Header account-management reads the step-up proof token from. Not a proto
// field: this service logs query parameters and the two DELETE routes have no
// body mapping, so the token would be read from the query string and written to
// our own logs.
const SECOND_FACTOR_PROOF_TOKEN_HEADER = 'second-factor-proof-token';

// Generated mutation hooks take `RequestInit` as their second argument and
// instant-api-hooks-core merges caller headers last, so the proof token needs no
// codegen and no generated request field.
function stepUpRequestInit(actionProofToken?: string): RequestInit | undefined {
  return actionProofToken
    ? { headers: { [SECOND_FACTOR_PROOF_TOKEN_HEADER]: actionProofToken } }
    : undefined;
}

// Janus-minted, single-use proof token for a mutation touching a step-up gated
// role. Never log or persist it.
type ActionProofTokenParam = {
  actionProofToken?: string;
};

type InviteMemberParams = ActionProofTokenParam & {
  email: string;
  roles: MemberRole[];
  legalEntityIds: string[];
  legalEntityRoles?: MemberRolesForEntity[];
};

// No readOnlyEntityIds here: the single-LE tray gates the whole submission on
// canEditEntity at the component level (EditMember.tsx), so an unauthorized
// entity never reaches this call. Contrast with UpdateMemberRolesPerEntityParams,
// where the multi-LE tray lists entities the user can view but not manage, so
// the mutation itself must exclude them.
type UpdateMemberRolesParams = ActionProofTokenParam & {
  memberUuid: string;
  selectedEntityIds: string[];
  roles: MemberRole[];
};

type UpdateMemberRolesPerEntityParams = ActionProofTokenParam & {
  memberUuid: string;
  legalEntityRoles: MemberRolesForEntity[];
  /**
   * Entities the current user may see but not manage. Excluded from both
   * legalEntityRoles and removedLegalEntityIds so an unauthorized entity can
   * never fail the whole request.
   */
  readOnlyEntityIds?: string[];
};

type RemoveMemberFromOrgParams = ActionProofTokenParam & {
  memberUuid: string;
};

export function useTeamApi(organizationId: string) {
  const { selectedOrganization } = useOrganization();
  const withOptimisticWorkspaceMembersUpdates = useWithOptimisticWorkspaceMembersUpdates({
    organizationId,
  });
  const refreshWorkspaceMembers = useRefreshWorkspaceMembers({ organizationId });
  const bulkCancelInvites = useBulkCancelInvites();
  const cancelPendingRoleChange = useCancelPendingRoleChange();
  const addOrganizationMember = useAddOrganizationMember();
  const removeOrganizationMember = useRemoveOrganizationMember();
  const updateOrganizationMemberRole = useUpdateOrganizationMemberRole();

  const entityIdToName = useMemo(
    () =>
      new Map(
        (selectedOrganization?.legalEntities ?? []).map((le) => [
          le.legalEntityId,
          le.legalEntityName,
        ]),
      ),
    [selectedOrganization?.legalEntities],
  );

  // Wraps withOptimisticWorkspaceMembersUpdates with consistent error handling:
  // refreshes cache and logs on failure, then re-throws for component-level UI handling.
  const runWithOptimisticUpdate = useCallback(
    async (context: string, mutationFn: MutationFn) => {
      try {
        await withOptimisticWorkspaceMembersUpdates(mutationFn);
      } catch (err) {
        void refreshWorkspaceMembers();
        logError(err, { context, isHandled: true, isUserVisible: true });
        throw err;
      }
    },
    [withOptimisticWorkspaceMembersUpdates, refreshWorkspaceMembers],
  );

  const cancelInvite = useCallback(
    async ({ userId, email, legalEntityIds }: CancelInviteParams) => {
      await runWithOptimisticUpdate('TeamApi.cancelInvite', (currentData) => {
        const canceledLegalEntityIds = new Set(legalEntityIds);
        const optimisticData = {
          ...currentData,
          pendingInvites: currentData?.pendingInvites?.filter(
            (invite) =>
              invite.email !== email ||
              invite.legalEntityId == null ||
              !canceledLegalEntityIds.has(invite.legalEntityId),
          ),
        };

        const request = async () => {
          const response = await bulkCancelInvites({ userId, legalEntityIds });
          throwIfError(response);

          // BulkCancelInvites returns HTTP 200 even when an individual invite
          // cancellation fails; the failure is reported per-item in `results`.
          const failedResult = response.results?.find((result) => result.success !== true);
          if (failedResult) {
            throw new Error(failedResult.error || 'Failed to cancel invite');
          }

          return response;
        };

        return [optimisticData, request()];
      });
    },
    [runWithOptimisticUpdate, bulkCancelInvites],
  );

  // "Revoke admin access": CancelPendingRoleChange cancels every role the member
  // still has in its grace period, on every legal entity of the organization.
  // Effective roles stay, including the compensating viewer granted alongside a
  // pending admin.
  const cancelPendingRoles = useCallback(
    async (userUuid: string): Promise<CancelPendingRolesOutcome> => {
      let outcome: CancelPendingRolesOutcome = 'cancelled';

      await runWithOptimisticUpdate('TeamApi.cancelPendingRoles', (currentData) => {
        const knownRequestIds = getPendingRequestIds(
          currentData?.members?.find((m) => m.userUuid === userUuid),
        );

        // A pending role is listed only in `legalEntityRolesDetailed`, so the
        // optimistic update drops the non-effective entries there.
        const withoutPendingRoles = (
          member: CoinbaseEngUsersAccountManagementApiApiWorkspaceMember,
        ): CoinbaseEngUsersAccountManagementApiApiWorkspaceMember => ({
          ...member,
          legalEntityRolesDetailed: member.legalEntityRolesDetailed?.map((entityRoles) => ({
            ...entityRoles,
            rolesDetailed: entityRoles.rolesDetailed?.filter(
              ({ activationState }) =>
                activationState === RoleActivationState.ROLE_ACTIVATION_STATE_EFFECTIVE,
            ),
          })),
        });

        const optimisticData = {
          ...currentData,
          members: currentData?.members?.map((m) =>
            m.userUuid === userUuid ? withoutPendingRoles(m) : m,
          ),
        };

        const request = async () => {
          const response = await cancelPendingRoleChange({ organizationId, userUuid });
          if (response instanceof ApiError && response.status === 403) {
            throw new Error(CANCEL_PENDING_ROLE_ERROR_CODES.permissionDenied, { cause: response });
          }
          throwIfError(response);
          outcome = getCancelPendingRolesOutcome(response.results, knownRequestIds);
          return response;
        };

        return [optimisticData, request()];
      });

      return outcome;
    },
    [runWithOptimisticUpdate, cancelPendingRoleChange, organizationId],
  );

  const inviteMember = useCallback(
    async ({
      email,
      roles,
      legalEntityIds,
      legalEntityRoles,
      actionProofToken,
    }: InviteMemberParams) => {
      await runWithOptimisticUpdate('TeamApi.inviteMember', (currentData) => {
        const legalEntityRolesById = new Map(
          (legalEntityRoles ?? []).map((legalEntityRole) => [
            legalEntityRole.legalEntityId,
            legalEntityRole.roles,
          ]),
        );
        const hasLegalEntityRoles = (legalEntityRoles?.length ?? 0) > 0;
        const optimisticLegalEntityIds = hasLegalEntityRoles
          ? legalEntityRoles?.map((legalEntityRole) => legalEntityRole.legalEntityId) ?? []
          : legalEntityIds;
        const requestLegalEntityIds = hasLegalEntityRoles ? [] : legalEntityIds;

        const optimisticInvites = optimisticLegalEntityIds.map((entityId) => ({
          roles: legalEntityRolesById.get(entityId) ?? roles,
          legalEntityId: entityId,
          createdAt: new Date().toISOString(),
        }));

        const optimisticData = {
          ...currentData,
          pendingInvites: [...(currentData?.pendingInvites ?? []), ...optimisticInvites],
        };

        const request = async () => {
          const response = await addOrganizationMember(
            {
              member: { email, roles },
              legalEntityIds: requestLegalEntityIds,
              legalEntityRoles,
            },
            stepUpRequestInit(actionProofToken),
          );
          throwIfError(response);
          return response;
        };

        return [optimisticData, request()];
      });
    },
    [runWithOptimisticUpdate, addOrganizationMember],
  );

  const updateMemberRoles = useCallback(
    async ({ memberUuid, selectedEntityIds, roles, actionProofToken }: UpdateMemberRolesParams) => {
      await runWithOptimisticUpdate('TeamApi.updateMemberRoles', (currentData) => {
        // Entities where all roles were removed should be treated as deletions, not updates.
        // Calling PUT /role with an empty roles array causes a backend error.
        const emptyRoleEntityIds = roles.length === 0 ? selectedEntityIds : [];
        const emptyRoleSet = new Set(emptyRoleEntityIds);

        const updatedMembers = currentData?.members?.map((m) => {
          if (m.userUuid !== memberUuid) return m;
          const currentLegalEntityRoles = m.legalEntityRoles ?? [];
          const nextLegalEntityRoles = selectedEntityIds
            .filter((entityId) => !emptyRoleSet.has(entityId))
            .map((entityId) => {
              const existingRole = currentLegalEntityRoles.find(
                (ler) => ler.legalEntityId === entityId,
              );
              // Newly-added entities have no existingRole yet, so their optimistic
              // display name comes from the org's legal entity list instead.
              const legalEntityName = existingRole?.legalEntityName ?? entityIdToName.get(entityId);
              return { ...existingRole, legalEntityId: entityId, legalEntityName, roles };
            });
          return { ...m, legalEntityRoles: nextLegalEntityRoles };
        });

        const currentRolesByEntityId = getCurrentRolesByEntityId(currentData, memberUuid);
        const removedEntityIds = [...currentRolesByEntityId.keys()].filter(
          (id) => !selectedEntityIds.includes(id),
        );

        const request = async () => {
          const entitiesToUpdate =
            roles.length === 0
              ? []
              : selectedEntityIds.filter((entityId) =>
                  haveRolesChanged(currentRolesByEntityId.get(entityId), roles),
                );

          // Deselected entities and entities whose last role was removed are
          // revoked. If the last entity is revoked, the backend removes org
          // membership as a result.
          const updatedEntitySet = new Set(entitiesToUpdate);
          const heldEntityIds = new Set(currentRolesByEntityId.keys());
          const removedLegalEntityIds = dedupe([...removedEntityIds, ...emptyRoleEntityIds]).filter(
            (entityId) => heldEntityIds.has(entityId) && !updatedEntitySet.has(entityId),
          );

          if (entitiesToUpdate.length === 0 && removedLegalEntityIds.length === 0) {
            return;
          }

          try {
            const response = await updateOrganizationMemberRole(
              {
                memberUuid,
                legalEntityRoles: entitiesToUpdate.map((legalEntityId) => ({
                  legalEntityId,
                  roles,
                })),
                removedLegalEntityIds,
              },
              stepUpRequestInit(actionProofToken),
            );
            throwIfError(response);
          } catch (error) {
            // A proof-token rejection has to reach the caller unwrapped, or the
            // prefix it re-prompts on is lost in the generic error below.
            if (isStepUpProofTokenError(error)) {
              throw error;
            }
            throw new Error('Error updating membership', { cause: error });
          }
        };

        return [{ ...currentData, members: updatedMembers }, request()];
      });
    },
    [runWithOptimisticUpdate, updateOrganizationMemberRole, entityIdToName],
  );

  const updateMemberRolesPerEntity = useCallback(
    async ({
      memberUuid,
      legalEntityRoles,
      readOnlyEntityIds,
      actionProofToken,
    }: UpdateMemberRolesPerEntityParams) => {
      await runWithOptimisticUpdate('TeamApi.updateMemberRolesPerEntity', (currentData) => {
        const readOnlySet = new Set(readOnlyEntityIds ?? []);
        const rolesById = new Map(legalEntityRoles.map((ler) => [ler.legalEntityId, ler.roles]));

        const currentRolesByEntityId = getCurrentRolesByEntityId(currentData, memberUuid);

        // LEs absent from the new list or present with empty roles should be
        // removed. Removal is explicit, so only LEs the member actually holds
        // are named.
        const newEntityIds = new Set(legalEntityRoles.map((ler) => ler.legalEntityId));
        const visibleEntityIds = new Set(
          (selectedOrganization?.legalEntities ?? []).map(({ legalEntityId }) => legalEntityId),
        );
        const delistedEntityIds = [...currentRolesByEntityId.keys()].filter(
          (id) => visibleEntityIds.has(id) && !newEntityIds.has(id) && !readOnlySet.has(id),
        );
        const emptyRoleEntityIds = legalEntityRoles
          .filter(({ roles }) => roles.length === 0)
          .map(({ legalEntityId }) => legalEntityId)
          .filter((id) => !readOnlySet.has(id) && currentRolesByEntityId.has(id));
        const removedEntityIds = dedupe([...delistedEntityIds, ...emptyRoleEntityIds]);

        const updatedMembers = currentData?.members?.map((m) => {
          if (m.userUuid !== memberUuid) return m;
          const removedSet = new Set(removedEntityIds);
          const nextLegalEntityRoles = (m.legalEntityRoles ?? [])
            .filter((ler) => !removedSet.has(ler.legalEntityId ?? ''))
            .map((ler) => {
              const newRoles = rolesById.get(ler.legalEntityId ?? '');
              return newRoles ? { ...ler, roles: newRoles } : ler;
            });
          return { ...m, legalEntityRoles: nextLegalEntityRoles };
        });

        const request = async () => {
          const entitiesToUpdate = legalEntityRoles.filter(({ legalEntityId, roles }) => {
            if (roles.length === 0) return false; // removals handled separately
            if (readOnlySet.has(legalEntityId)) return false;
            return haveRolesChanged(currentRolesByEntityId.get(legalEntityId), roles);
          });

          const updatedEntitySet = new Set(entitiesToUpdate.map((ler) => ler.legalEntityId));
          const removedLegalEntityIds = removedEntityIds.filter(
            (entityId) => !updatedEntitySet.has(entityId),
          );

          if (entitiesToUpdate.length === 0 && removedLegalEntityIds.length === 0) {
            // The caller (form) should not invoke this with no actual changes —
            // log so we can spot stale form-validation bugs without surfacing to the user.
            logError(new Error('updateMemberRolesPerEntity called with no changes'), {
              context: 'TeamApi.updateMemberRolesPerEntity',
              isHandled: true,
              isUserVisible: false,
            });
            return;
          }

          try {
            const response = await updateOrganizationMemberRole(
              {
                memberUuid,
                legalEntityRoles: entitiesToUpdate,
                removedLegalEntityIds,
              },
              stepUpRequestInit(actionProofToken),
            );
            throwIfError(response);
          } catch (error) {
            // A proof-token rejection has to reach the caller unwrapped, or the
            // prefix it re-prompts on is lost in the generic error below.
            if (isStepUpProofTokenError(error)) {
              throw error;
            }
            throw new Error('Error updating membership', { cause: error });
          }
        };

        return [{ ...currentData, members: updatedMembers }, request()];
      });
    },
    [runWithOptimisticUpdate, updateOrganizationMemberRole, selectedOrganization?.legalEntities],
  );

  const removeMemberFromOrg = useCallback(
    async ({ memberUuid, actionProofToken }: RemoveMemberFromOrgParams) => {
      await runWithOptimisticUpdate('TeamApi.removeMemberFromOrg', (currentData) => {
        const optimisticData = {
          ...currentData,
          members: currentData?.members?.filter((m) => m.userUuid !== memberUuid),
        };

        const request = async () => {
          const response = await removeOrganizationMember(
            { memberUuid, organizationId },
            stepUpRequestInit(actionProofToken),
          );
          throwIfError(response);
          return response;
        };

        return [optimisticData, request()];
      });
    },
    [runWithOptimisticUpdate, removeOrganizationMember, organizationId],
  );

  return {
    cancelInvite,
    cancelPendingRoles,
    inviteMember,
    updateMemberRoles,
    updateMemberRolesPerEntity,
    removeMemberFromOrg,
  };
}
